import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  message
} from 'antd';
import {
  BookOutlined,
  CrownOutlined,
  FireOutlined,
  GiftOutlined,
  HeartOutlined,
  ReadOutlined,
  ReloadOutlined,
  RocketOutlined,
  ShopOutlined,
  StarOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import studentShopAPI from '../../services/studentShopAPI';
import { useAuth } from '../../contexts/AuthContext';

const { TextArea } = Input;

const ITEM_TYPE_LABELS = {
  homework_exemption: '作业豁免',
  physical: '实物奖品',
  privilege: '班级权益',
  other: '其他'
};

const RECORD_STATUS_LABELS = {
  pending: '待发放',
  fulfilled: '已发放',
  used: '已使用',
  cancelled: '已取消'
};

const RECORD_STATUS_COLORS = {
  pending: 'gold',
  fulfilled: 'blue',
  used: 'green',
  cancelled: 'default'
};

const iconOptions = [
  { value: 'GiftOutlined', label: '礼物', icon: <GiftOutlined /> },
  { value: 'StarOutlined', label: '星星', icon: <StarOutlined /> },
  { value: 'BookOutlined', label: '书本', icon: <BookOutlined /> },
  { value: 'RocketOutlined', label: '火箭', icon: <RocketOutlined /> },
  { value: 'CrownOutlined', label: '皇冠', icon: <CrownOutlined /> },
  { value: 'FireOutlined', label: '火焰', icon: <FireOutlined /> },
  { value: 'HeartOutlined', label: '爱心', icon: <HeartOutlined /> },
  { value: 'ThunderboltOutlined', label: '闪电', icon: <ThunderboltOutlined /> },
  { value: 'ReadOutlined', label: '阅读', icon: <ReadOutlined /> }
];

const iconMap = {
  GiftOutlined: <GiftOutlined />,
  StarOutlined: <StarOutlined />,
  BookOutlined: <BookOutlined />,
  RocketOutlined: <RocketOutlined />,
  CrownOutlined: <CrownOutlined />,
  FireOutlined: <FireOutlined />,
  HeartOutlined: <HeartOutlined />,
  ThunderboltOutlined: <ThunderboltOutlined />,
  ReadOutlined: <ReadOutlined />
};

const defaultRecordPagination = {
  current: 1,
  pageSize: 10,
  total: 0
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('zh-CN', { hour12: false });
};

const getClassLabel = (item) => item?.class_name || item?.name || '未命名班级';

const StudentShopManagement = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState({ item_types: [], record_statuses: [] });
  const [itemsLoading, setItemsLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordFilters, setRecordFilters] = useState({ status: undefined, studentId: undefined });
  const [recordPagination, setRecordPagination] = useState(defaultRecordPagination);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);
  const [exchangingItem, setExchangingItem] = useState(null);
  const [savingExchange, setSavingExchange] = useState(false);
  const [form] = Form.useForm();
  const [exchangeForm] = Form.useForm();

  useEffect(() => {
    loadMeta();
    loadClasses();
  }, []);

  useEffect(() => {
    if (!selectedClassId) {
      setItems([]);
      setRecords([]);
      setStudents([]);
      return;
    }

    loadStudents(selectedClassId);
    loadItems(selectedClassId);
    loadRecords(selectedClassId, { ...defaultRecordPagination }, recordFilters);
  }, [selectedClassId]);

  const stats = useMemo(() => {
    const activeItems = items.filter((item) => Number(item.is_active) === 1);
    const pendingCount = records.filter((item) => item.status === 'pending').length;
    return {
      totalItems: items.length,
      activeItems: activeItems.length,
      pendingCount
    };
  }, [items, records]);

  const loadMeta = async () => {
    try {
      const response = await studentShopAPI.getMeta();
      if (response.data.success) {
        setMeta(response.data.data || { item_types: [], record_statuses: [] });
      }
    } catch (error) {
      message.error('获取商店配置失败');
    }
  };

  const loadClasses = async () => {
    try {
      const response = await api.get('/classes');

      if (response.data.success) {
        const list = response.data.data || [];
        setClasses(list);
        if (list.length > 0) {
          setSelectedClassId((current) => current || list[0].id);
        }
      }
    } catch (error) {
      message.error('获取班级列表失败');
    }
  };

  const loadStudents = async (classId) => {
    try {
      const response = await api.get('/students', { params: { classId } });
      if (response.data.success) {
        setStudents(response.data.data || []);
      }
    } catch (error) {
      setStudents([]);
      message.error('获取学生列表失败');
    }
  };

  const loadItems = async (classId = selectedClassId) => {
    if (!classId) {
      return;
    }

    setItemsLoading(true);
    try {
      const response = await studentShopAPI.getClassItems(classId, { includeInactive: 1 });
      if (response.data.success) {
        setItems(response.data.data || []);
      } else {
        message.error(response.data.message || '获取商店商品失败');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || '获取商店商品失败');
    } finally {
      setItemsLoading(false);
    }
  };

  const loadRecords = async (
    classId = selectedClassId,
    pagination = recordPagination,
    filters = recordFilters
  ) => {
    if (!classId) {
      return;
    }

    setRecordsLoading(true);
    try {
      const response = await studentShopAPI.getClassRecords(classId, {
        page: pagination.current,
        pageSize: pagination.pageSize,
        status: filters.status || undefined,
        studentId: filters.studentId || undefined
      });

      if (response.data.success) {
        setRecords(response.data.data || []);
        setRecordPagination({
          current: response.data.pagination?.current || pagination.current,
          pageSize: response.data.pagination?.pageSize || pagination.pageSize,
          total: response.data.pagination?.total || 0
        });
      } else {
        message.error(response.data.message || '获取兑换记录失败');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || '获取兑换记录失败');
    } finally {
      setRecordsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    form.setFieldsValue({
      name: '',
      description: '',
      icon: 'GiftOutlined',
      item_type: 'physical',
      price_points: 1,
      stock: -1,
      is_active: true,
      sort_order: 0
    });
    setItemModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      icon: record.icon || 'GiftOutlined',
      item_type: record.item_type || 'physical',
      price_points: Number(record.price_points || 1),
      stock: Number(record.stock ?? -1),
      is_active: Number(record.is_active) === 1,
      sort_order: Number(record.sort_order || 0)
    });
    setItemModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!selectedClassId) {
      message.warning('请先选择班级');
      return;
    }

    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        class_id: selectedClassId,
        is_active: values.is_active ? 1 : 0
      };

      setSavingItem(true);
      if (editingItem) {
        await studentShopAPI.updateItem(editingItem.id, payload);
        message.success('商品已更新');
      } else {
        await studentShopAPI.createItem(payload);
        message.success('商品已创建');
      }

      setItemModalOpen(false);
      loadItems(selectedClassId);
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '保存商品失败');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDisableItem = async (record) => {
    try {
      await studentShopAPI.deleteItem(record.id);
      message.success('商品已下架');
      loadItems(selectedClassId);
    } catch (error) {
      message.error(error?.response?.data?.message || '下架商品失败');
    }
  };

  const openExchangeModal = (record) => {
    setExchangingItem(record);
    exchangeForm.setFieldsValue({
      student_id: undefined,
      remark: ''
    });
    setExchangeModalOpen(true);
  };

  const handleExchangeItem = async () => {
    if (!selectedClassId || !exchangingItem) {
      return;
    }

    try {
      const values = await exchangeForm.validateFields();
      setSavingExchange(true);
      await studentShopAPI.exchangeItem({
        class_id: selectedClassId,
        item_id: exchangingItem.id,
        student_id: values.student_id,
        remark: values.remark || ''
      });
      message.success('兑换成功，已扣除学生积分并生成记录');
      setExchangeModalOpen(false);
      loadItems(selectedClassId);
      loadRecords(selectedClassId, { ...defaultRecordPagination }, recordFilters);
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '兑换失败');
    } finally {
      setSavingExchange(false);
    }
  };

  const handleRecordStatusChange = async (record, status) => {
    try {
      await studentShopAPI.updateRecordStatus(record.id, { status });
      message.success('兑换记录状态已更新');
      loadRecords(selectedClassId);
    } catch (error) {
      message.error(error?.response?.data?.message || '更新兑换状态失败');
    }
  };

  const handleRefund = async (record) => {
    try {
      await studentShopAPI.refundRecord(record.id, { reason: '后台取消兑换并退回积分' });
      message.success('已退回积分');
      loadRecords(selectedClassId);
      loadItems(selectedClassId);
    } catch (error) {
      message.error(error?.response?.data?.message || '退回积分失败');
    }
  };

  const itemColumns = [
    {
      title: '商品',
      dataIndex: 'name',
      render: (_, record) => (
        <Space>
          <span style={{ fontSize: 16 }}>{iconMap[record.icon] || <GiftOutlined />}</span>
          <span>{record.name}</span>
        </Space>
      )
    },
    {
      title: '类型',
      dataIndex: 'item_type',
      width: 130,
      render: (value) => ITEM_TYPE_LABELS[value] || value || '-'
    },
    {
      title: '来源',
      dataIndex: 'source_type',
      width: 110,
      render: (value) => value === 'system' ? <Tag color="blue">系统商品</Tag> : <Tag color="green">自建商品</Tag>
    },
    {
      title: '积分',
      dataIndex: 'price_points',
      width: 100
    },
    {
      title: '库存',
      dataIndex: 'stock',
      width: 100,
      render: (value) => Number(value) < 0 ? '不限' : value
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 100,
      render: (value) => Number(value) === 1 ? <Tag color="green">上架中</Tag> : <Tag>已下架</Tag>
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 90
    },
    {
      title: '说明',
      dataIndex: 'description',
      ellipsis: true,
      render: (value) => value || '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" disabled={Number(record.is_active) !== 1} onClick={() => openExchangeModal(record)}>
            兑换
          </Button>
          {record.source_type !== 'system' && (
            <Button type="link" onClick={() => openEditModal(record)}>
              编辑
            </Button>
          )}
          {record.source_type !== 'system' && Number(record.is_active) === 1 && (
            <Popconfirm title="确认下架该商品？" onConfirm={() => handleDisableItem(record)}>
              <Button type="link" danger>
                下架
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const recordColumns = [
    {
      title: '兑换时间',
      dataIndex: 'created_at',
      width: 170,
      render: (value) => formatDateTime(value)
    },
    {
      title: '学生',
      dataIndex: 'student_name',
      width: 120
    },
    {
      title: '商品',
      dataIndex: 'item_name'
    },
    {
      title: '消耗积分',
      dataIndex: 'cost_points',
      width: 110
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => (
        <Tag color={RECORD_STATUS_COLORS[value] || 'default'}>
          {RECORD_STATUS_LABELS[value] || value || '-'}
        </Tag>
      )
    },
    {
      title: '备注',
      dataIndex: 'remark',
      render: (value) => value || '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small" wrap>
          {record.status === 'pending' && (
            <Button type="link" onClick={() => handleRecordStatusChange(record, 'fulfilled')}>
              标记发放
            </Button>
          )}
          {(record.status === 'pending' || record.status === 'fulfilled') && (
            <Button type="link" onClick={() => handleRecordStatusChange(record, 'used')}>
              标记使用
            </Button>
          )}
          {record.status !== 'cancelled' && !record.refund_point_record_id && (
            <Popconfirm title="确认退回该次兑换积分？" onConfirm={() => handleRefund(record)}>
              <Button type="link" danger>
                退回积分
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Space><ShopOutlined />商店中心</Space>}
        extra={(
          <Space wrap>
            <Select
              style={{ width: 220 }}
              value={selectedClassId}
              onChange={(value) => {
                setRecordFilters({ status: undefined, studentId: undefined });
                setRecordPagination(defaultRecordPagination);
                setSelectedClassId(value);
              }}
              placeholder="选择班级"
            >
              {classes.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {getClassLabel(item)}
                </Select.Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => {
              loadItems(selectedClassId);
              loadRecords(selectedClassId, recordPagination, recordFilters);
            }}>
              刷新
            </Button>
            <Button type="primary" onClick={openCreateModal}>
              新建商品
            </Button>
          </Space>
        )}
      >
        <Space size={24} style={{ marginBottom: 20 }} wrap>
          <Statistic title="商品总数" value={stats.totalItems} />
          <Statistic title="上架商品" value={stats.activeItems} />
          <Statistic title="待处理兑换" value={stats.pendingCount} />
        </Space>

        <Tabs
          items={[
            {
              key: 'items',
              label: '商品管理',
              children: (
                <Table
                  rowKey="id"
                  loading={itemsLoading}
                  columns={itemColumns}
                  dataSource={items}
                  pagination={false}
                  scroll={{ x: 980 }}
                />
              )
            },
            {
              key: 'records',
              label: '兑换记录',
              children: (
                <>
                  <Space style={{ marginBottom: 16 }} wrap>
                    <Select
                      allowClear
                      style={{ width: 200 }}
                      placeholder="按学生筛选"
                      value={recordFilters.studentId}
                      onChange={(value) => {
                        const nextFilters = { ...recordFilters, studentId: value };
                        setRecordFilters(nextFilters);
                        loadRecords(selectedClassId, { ...recordPagination, current: 1 }, nextFilters);
                      }}
                    >
                      {students.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.student_name}
                        </Select.Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      style={{ width: 180 }}
                      placeholder="按状态筛选"
                      value={recordFilters.status}
                      onChange={(value) => {
                        const nextFilters = { ...recordFilters, status: value };
                        setRecordFilters(nextFilters);
                        loadRecords(selectedClassId, { ...recordPagination, current: 1 }, nextFilters);
                      }}
                    >
                      {(meta.record_statuses || []).map((item) => (
                        <Select.Option key={item.value} value={item.value}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Space>
                  <Table
                    rowKey="id"
                    loading={recordsLoading}
                    columns={recordColumns}
                    dataSource={records}
                    scroll={{ x: 1080 }}
                    pagination={recordPagination}
                    onChange={(pagination) => {
                      const nextPagination = {
                        current: pagination.current || 1,
                        pageSize: pagination.pageSize || defaultRecordPagination.pageSize,
                        total: recordPagination.total
                      };
                      loadRecords(selectedClassId, nextPagination, recordFilters);
                    }}
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      <Modal
        title={editingItem ? '编辑商品' : '新建商品'}
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        onOk={handleSaveItem}
        confirmLoading={savingItem}
        okText="保存"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="商品名称"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input maxLength={100} placeholder="请输入商品名称" />
          </Form.Item>
          <Form.Item name="description" label="商品说明">
            <TextArea rows={3} maxLength={500} placeholder="可填写兑换规则、使用说明等" />
          </Form.Item>
          <Form.Item
            name="icon"
            label="图标"
            rules={[{ required: true, message: '请选择图标' }]}
          >
            <Select
              options={iconOptions.map((item) => ({
                value: item.value,
                label: (
                  <Space>
                    {item.icon}
                    <span>{item.label}</span>
                  </Space>
                )
              }))}
            />
          </Form.Item>
          <Form.Item
            name="item_type"
            label="商品类型"
            rules={[{ required: true, message: '请选择商品类型' }]}
          >
            <Select
              options={(meta.item_types || []).map((item) => ({
                value: item.value,
                label: item.label
              }))}
            />
          </Form.Item>
          <Form.Item
            name="price_points"
            label="兑换积分"
            rules={[{ required: true, message: '请输入兑换积分' }]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="stock"
            label="库存"
            extra="输入 -1 代表不限库存"
            rules={[{ required: true, message: '请输入库存' }]}
          >
            <InputNumber min={-1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="立即上架" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="兑换商品"
        open={exchangeModalOpen}
        onCancel={() => setExchangeModalOpen(false)}
        onOk={handleExchangeItem}
        confirmLoading={savingExchange}
        okText="确认兑换"
        destroyOnClose
      >
        <Form form={exchangeForm} layout="vertical">
          <Form.Item label="商品">
            <Space>
              <span style={{ fontSize: 16 }}>{iconMap[exchangingItem?.icon] || <GiftOutlined />}</span>
              <span>{exchangingItem?.name}</span>
              <Tag color="orange">{Number(exchangingItem?.price_points || 0)} 积分</Tag>
            </Space>
          </Form.Item>
          <Form.Item
            name="student_id"
            label="兑换学生"
            rules={[{ required: true, message: '请选择兑换学生' }]}
          >
            <Select
              showSearch
              placeholder="请选择学生"
              optionFilterProp="children"
            >
              {students.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.student_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={3} maxLength={500} placeholder="可填写发放说明或使用规则" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentShopManagement;
